/**
 * SHARED leveraged / inverse ETF exclusion predicate.
 *
 * ONE robust predicate used by BOTH the live Watcher (entry gate) and the backtest
 * universe, so the two stay identical. Leveraged (2×/3×) and inverse daily-reset ETFs
 * are volatility-decay products designed for single-day exposure — structurally wrong
 * for the Watcher's multi-day support/breakout holds. (Owner directive 2026-07-01:
 * open TQQQ/SPXL/TECL/QLD/SSO/TSMX/MULL/BTCZ/SBIT are grandfathered; block FUTURE entries.)
 *
 * Detection is NAME-first (so it catches brand-new tickers automatically) with a small
 * curated symbol fallback for when the `stocks.name` is missing/stale.
 *
 * Toggle: WATCHER_BLOCK_LEVERAGED_ETF (default ON per owner). Set to '0'/'false'/'off'
 * to disable the live block WITHOUT a redeploy. The backtest ALWAYS applies the predicate
 * (a clean research universe is the point) regardless of the env flag.
 */

/**
 * Positive leverage/inverse signatures in an ETF long name. Case-insensitive.
 *   \b[23]x\b   — "2X"/"3X" as a standalone token (Direxion/GraniteShares/T-Rex/Tradr/Defiance)
 *   ultrapro    — ProShares UltraPro (3×)
 *   ultrashort  — ProShares UltraShort (−2× inverse) — see FIXED-INCOME guard below
 *   \bultra\b   — ProShares Ultra (2×) — see FIXED-INCOME guard below
 *   inverse / -1x               — single-inverse products
 *   \bbull\b / \bbear\b         — directional leveraged single-stock/index ETFs
 *   leverag(e/ed)               — explicit "Leveraged" / "Leverage Shares"
 */
const LEVERAGE_NAME_RE =
  /\b[23]x\b|ultrapro|ultra\s*pro|ultrashort|ultra\s*short|\bultra\b|\binverse\b|-1x\b|\bbull\b|\bbear\b|\bleverag/i;

/**
 * FIXED-INCOME guard: "Ultra Short" is overloaded — it also names ultra-short-DURATION
 * bond/money-market funds (Vanguard Ultra-Short Bond, PIMCO Ultra Short Government, etc.),
 * which are NOT leveraged/inverse. When "ultra short" co-occurs with a fixed-income term
 * AND no hard-leverage token (2x/3x/inverse/bull/bear/leverage) is present, it is NOT a
 * decay-ETF. 44 such funds exist in the universe; excluding them keeps the predicate honest.
 */
const ULTRA_SHORT_RE = /ultra[\s-]{0,3}short/i;
const FIXED_INCOME_RE =
  /\b(bond|municipal|muni|income|government|govt|treasury|duration|fixed[\s-]?income|money[\s-]?market)\b/i;
/** Hard leverage tokens that OVERRIDE the fixed-income guard (a real leveraged product). */
const HARD_LEVERAGE_RE = /\b[23]x\b|\binverse\b|\bbull\b|\bbear\b|-1x\b|\bleverag|ultrapro/i;

/**
 * Curated fallback of known decay-ETF symbols. Used only when the name is missing/blank.
 * Includes the 9 already-open positions the owner flagged, plus the common index 2×/3× set.
 * The NAME regex is the primary detector — this list is a belt-and-suspenders backstop.
 */
export const KNOWN_LEVERAGED_INVERSE_SYMBOLS: ReadonlySet<string> = new Set(
  [
    // The 9 flagged open positions (2026-07-01)
    'TQQQ', 'SPXL', 'TECL', 'QLD', 'SSO', 'TSMX', 'MULL', 'BTCZ', 'SBIT',
    // Common index leveraged/inverse
    'SQQQ', 'SOXL', 'SOXS', 'TNA', 'TZA', 'SPXS', 'UPRO', 'SDOW', 'UDOW',
    'FAS', 'FAZ', 'LABU', 'LABD', 'YINN', 'YANG', 'NUGT', 'DUST', 'JNUG', 'JDST',
    'BOIL', 'KOLD', 'GUSH', 'DRIP', 'UVXY', 'SVXY', 'VIXY', 'SVIX', 'UVIX',
    'BITX', 'BITU', 'ETHU', 'ETHD', 'MSTU', 'MSTX', 'MSTZ', 'NVDL', 'NVDU', 'NVDD',
    'TSLL', 'TSLQ', 'TSLS', 'CONL', 'AMDL', 'AMDU', 'AMZU', 'AMZD',
  ].map((s) => s.toUpperCase()),
);

/**
 * TRUE when `name` carries a leveraged/inverse ETF signature (name-based, catches new tickers).
 * Standalone-testable; no env/DB dependency.
 */
export function nameLooksLeveragedOrInverse(name: string | null | undefined): boolean {
  if (!name) return false;
  const n = name.trim();
  if (n.length === 0) return false;
  if (!LEVERAGE_NAME_RE.test(n)) return false;
  // Fixed-income "ultra short bond" guard: reject the match ONLY when it is an ultra-short
  // fixed-income fund with no hard-leverage token.
  if (ULTRA_SHORT_RE.test(n) && FIXED_INCOME_RE.test(n) && !HARD_LEVERAGE_RE.test(n)) {
    return false;
  }
  return true;
}

/**
 * SHARED predicate: is this symbol a leveraged/inverse (decay-prone, daily-reset) ETF?
 *   @param symbol  ticker (case-insensitive)
 *   @param name    the `stocks.name` long name (preferred detector; may be null)
 *
 * Name signature is primary; the curated symbol fallback covers a missing/blank name.
 */
export function isLeveragedOrInverseETF(
  symbol: string | null | undefined,
  name?: string | null,
): boolean {
  if (nameLooksLeveragedOrInverse(name)) return true;
  if (symbol && KNOWN_LEVERAGED_INVERSE_SYMBOLS.has(symbol.trim().toUpperCase())) return true;
  return false;
}

/** Live-gate toggle. Default ON (owner directive). Env override, no redeploy required. */
export function watcherBlockLeveragedEtfEnabled(): boolean {
  const v = (process.env.WATCHER_BLOCK_LEVERAGED_ETF ?? '').trim().toLowerCase();
  if (v === '0' || v === 'false' || v === 'off' || v === 'no') return false;
  return true; // unset / anything else → ON
}
